/**
 * Volunteers — the one staff screen (M1 track D3, redesigned by Plan D12).
 *
 * Two sections, and the split is the point:
 *
 *   **New registrations** (above) — what people submitted, read-only. It is a
 *   work list: a submission leaves as soon as its volunteer is decided, so an
 *   empty panel means nothing is waiting.
 *
 *   **The directory** (below) — everyone Parinaam works with, and every action.
 *   Activate, reject, edit, delete a duplicate: all of it happens here, because
 *   this is the only surface that also covers volunteers entered by staff, who
 *   have no registration behind them at all.
 *
 * Before D12 the decision could also be made from a separate Registrations
 * screen. Two surfaces for one decision is how a small team learns to distrust
 * both, so that screen became the read-only archive it always should have been.
 *
 * The directory is ordered alphabetically and shows everyone by default — it is
 * a place to look people up, not a queue.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VOLUNTEER_STATUSES } from '@parinaam-vms/shared';
import type { VolunteerListItem } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Pager,
  SelectInput,
  StatusBadge,
  TextInput,
} from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useVolunteers } from '../../hooks/useVolunteers';
import { AddVolunteerForm } from './AddVolunteerForm';
import { BulkImportPanel } from './BulkImportPanel';
import { SubmissionsPanel } from './SubmissionsPanel';
import { countedTitle, formatDate, humaniseCode, orDash } from '../../utils/format';
import styles from './VolunteersPage.module.css';

const STATUS_OPTIONS = VOLUNTEER_STATUSES.map((status) => ({
  value: status,
  label: humaniseCode(status),
}));

export function VolunteersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const query = useVolunteers({
    search: search.trim() || undefined,
    status: status || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Volunteers"
        breadcrumbs={[CRUMB.volunteers]}
        actions={
          !adding &&
          !importing && (
            <div className={styles.headerActions}>
              {/* Not "Import from CSV" any more — it takes an Excel workbook
                  too, and the door has to say so or the feature may as well not
                  be there. */}
              <Button onClick={() => setImporting(true)}>Import volunteers</Button>
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add a volunteer
              </Button>
            </div>
          )
        }
      />

      {/*
        Staff entry, and since M3 the only way to record a corporate volunteer —
        a group never arrives through the public form.
      */}
      {adding && <AddVolunteerForm onClose={() => setAdding(false)} />}

      {/*
        Item 8: a corporate partner's whole list at once, when they have shared
        contact details. Each row becomes a pending volunteer linked to the
        partner; duplicates are skipped and reported.
      */}
      {importing && <BulkImportPanel onClose={() => setImporting(false)} />}

      {/*
        Plan D12: one screen. What arrived, read-only, above what staff do about
        it. The panel is a work list and empties itself; the directory below is
        where every action lives, because it is the only surface that also covers
        volunteers entered by staff, who have no registration behind them.
      */}
      <SubmissionsPanel />

      <Card title={countedTitle('Volunteer directory', query.data?.pagination?.total)}>
        <FilterBar label="Filter volunteers">
          <TextInput
            label="Search"
            placeholder="Name, code, email, phone or city"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <SelectInput
            label="Status"
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          />
        </FilterBar>

        {query.isPending ? (
          <LoadingState label="Loading volunteers…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No volunteers found"
            message={
              search || status
                ? 'No volunteer matches these filters.'
                : 'Registrations and staff entries appear here.'
            }
          />
        ) : (
          <>
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            <DataTable<VolunteerListItem>
              caption="Volunteers"
              rows={rows}
              getRowKey={(row) => row.volunteer_id}
              onRowClick={(row) => navigate(`/volunteers/${row.volunteer_id}`)}
              columns={[
                { key: 'code', header: 'ID', render: (row) => row.volunteer_code },
                {
                  key: 'name',
                  header: 'Name',
                  render: (row) => [row.first_name, row.last_name].filter(Boolean).join(' '),
                },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (row) => orDash(row.email ?? row.phone),
                },
                {
                  /*
                    Whether somebody volunteers on their own or through their
                    employer, and which employer.

                    Both fields were already being fetched and neither was shown,
                    so a corporate volunteer was indistinguishable from an
                    individual in the one place staff look people up. The
                    partner is the line beneath, not a column of its own: it is
                    only ever present on a corporate row.
                  */
                  key: 'kind',
                  header: 'Kind',
                  render: (row) => (
                    <>
                      {humaniseCode(row.category)}
                      {row.organization_name && (
                        <span className={styles.partner}>{row.organization_name}</span>
                      )}
                    </>
                  ),
                },
                { key: 'city', header: 'City', render: (row) => orDash(row.city) },
                {
                  key: 'registered',
                  header: 'Registered',
                  // Plan D11: a public registrant's volunteer row is created at
                  // submission, so created_at is the date they registered.
                  render: (row) => formatDate(row.created_at),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (row) => <StatusBadge status={row.status} />,
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  );
}
